import { CircularProgress, TableRow } from '@mui/material';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { OnlinePayment } from '../../../../shared/api/hscan/hospital';
import { useLocalIssuanceInPayment } from '../../../../shared/hooks/hospitalHook';
import { HscanTableCell } from '../../common/components/SortableTable';
import { handleCellStyle } from '../../common/components/SortableTable/RenderTable';
import {
  BlueStatusMessage,
  GreenStatusMessage,
  RedStatusMessage,
} from '../../common/components/styledComponents';
import { useModalityLabel } from '../../hooks/modalityHook';

const StudyIssuanceRequestsTableRow = styled(TableRow)`
  background: #f8f9fd;
`;

type LocalIssuanceStatus = 'PENDING' | 'COMPLETED' | 'FAILED';
interface LocalIssuanceSubTableColumns {
  studyDate: string;
  hospitalName: string;
  modality: string;
  studyDescription: string;
  status: LocalIssuanceStatus;
}
export interface AdminStudiesPaymentSubTableProps {
  payment: OnlinePayment;
}
export const AdminStudiesPaymentSubTable = ({
  payment,
}: AdminStudiesPaymentSubTableProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.payments.table.localIssuanceTable',
  });
  const modalityLabel = useModalityLabel();

  const { data, isFetching, isSuccess } =
    useAdminStudiesPaymentSubTableData(payment);

  const columnHelper = useMemo(
    () => createColumnHelper<LocalIssuanceSubTableColumns>(),
    [],
  );
  const cellOptions = useMemo(
    () => [
      columnHelper.display({
        id: '__PLACEHOLDER__',
        meta: {
          cellStyle: { background: 'white', borderBottom: { style: 'none' } },
          headerStyle: { background: 'white', borderBottom: { style: 'none' } },
        },
      }),
      columnHelper.accessor('studyDate', {
        header: `${t('studyDate')}`,
        cell: ({ getValue }) => moment(getValue()).format('YYYY.MM.DD'),
      }),
      columnHelper.accessor('hospitalName', {
        header: `${t('hospitalName')}`,
      }),
      columnHelper.accessor('modality', {
        header: `${t('modality')}`,
        cell: ({ getValue }) => modalityLabel(getValue()),
      }),
      columnHelper.accessor('studyDescription', {
        header: `${t('studyDescription')}`,
      }),
      columnHelper.accessor('status', {
        header: `${t('status')}`,
        cell: ({ getValue }) => {
          switch (getValue()) {
            case 'PENDING':
              return (
                <GreenStatusMessage>{`${t('pending')}`}</GreenStatusMessage>
              );
            case 'COMPLETED':
              return (
                <BlueStatusMessage>{`${t('completed')}`}</BlueStatusMessage>
              );
            default:
              return <RedStatusMessage>{`${t('failed')}`}</RedStatusMessage>;
          }
        },
      }),
      columnHelper.display({
        id: '__EMPTY_CELL_1__',
      }),
    ],
    [columnHelper, t, modalityLabel],
  );

  const table = useReactTable({
    data,
    columns: cellOptions,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {isSuccess ? (
        <>
          <StudyIssuanceRequestsTableRow>
            {table.getLeafHeaders().map(header => (
              <HscanTableCell
                key={header?.id}
                {...handleCellStyle(header.column.columnDef.meta?.headerStyle)}
                align="center">
                {
                  flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  ) as React.ReactNode
                }
              </HscanTableCell>
            ))}
          </StudyIssuanceRequestsTableRow>
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map(cell => (
                <HscanTableCell
                  key={cell.id}
                  {...handleCellStyle(cell.column.columnDef.meta?.cellStyle)}
                  align="center">
                  {
                    flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    ) as React.ReactNode
                  }
                </HscanTableCell>
              ))}
            </TableRow>
          ))}
        </>
      ) : (
        isFetching && (
          <TableRow>
            <HscanTableCell />
            <HscanTableCell colSpan={cellOptions.length} align="center">
              <CircularProgress />
            </HscanTableCell>
          </TableRow>
        )
      )}
    </>
  );
};

const useAdminStudiesPaymentSubTableData = (payment: OnlinePayment) => {
  const { id: paymentId, content, hospitalName } = payment;
  const {
    data: issuance,
    isFetching,
    isSuccess,
  } = useLocalIssuanceInPayment({
    paymentId,
  });

  const subData: LocalIssuanceSubTableColumns[] = useMemo(() => {
    return content.map(
      ({ modality, studyDate, studyDescription, studyInstanceUID }) => ({
        studyDate,
        hospitalName,
        modality,
        studyDescription,
        status: issuance
          ? // issuance.handledStudies.includes(`studies/${studyInstanceUID}`)
            issuance.studiesInBundle.length === issuance.handledStudies.length
            ? 'COMPLETED'
            : 'PENDING'
          : 'FAILED',
      }),
    );
  }, [content, hospitalName, issuance]);

  return {
    data: subData,
    isFetching,
    isSuccess,
  };
};
